import { groupBy } from 'lodash'
import moment from 'moment'
import { EditorView } from 'rete/types/view'
import { ConnectionView } from 'rete/types/view/connection'
import Vue from 'vue'

import { SelectOption } from '../../../../logic/types'
import { CustomInput, CustomOutput } from '../components'
import {
  ConnectionStatistic,
  ConnectionStatisticsData,
  CustomConnection,
  UserConnectionStatistic
} from './Connections/CustomConnection'
import { StartNode } from './Editor/StartNode/StartNode'
import { FlowStatisticsMode, FlowStatValueType } from './Editor/types'

type StatSetting = {
  mode: FlowStatisticsMode
  from: string
  to: string
  valueType: FlowStatValueType,
  user: {
    login: null | string
    id: null | number
  }
}

export abstract class FlowStatistics {

  static StartNode: StartNode | null = null;

  connectionsStatistics: null | Array<any> = null

  userConnectionsStatistics: null | Array<any> = null

  editorView: EditorView

  users: Array<SelectOption> | null = null

  statSetting: StatSetting = this.defaultStatSetting()

  selectedConnection: CustomConnection | null = null

  protected constructor(flowConnections: EditorView) {
    this.editorView = flowConnections
  }

  get defaultFrom() {
    return moment().add(-1, 'week').format('YYYY-MM-DD')
  }

  get defaultTo() {
    return moment().format('YYYY-MM-DD')
  }

  abstract reqConnectionsStatistics(): Promise<Array<any>>

  abstract reqConnectionStatistics(inputKey: number, output: number): Promise<Array<any>>

  abstract reqFlowUserStatistics(user: number): Promise<Array<any>>

  abstract getUserInfo(userData: string): Promise<any>

  findUserInfo(userData: string) {
    return this.getUserInfo(userData)
      .then(res => {
        if (res) {
          const user = res.find((l:any) => l.value === userData)

          if (user) {
            this.statSetting.user.login = user.info.login
            this.statSetting.user.id = user.info.id

            return true
          } else {
            return null
          }
        }
      })
  }

  defaultStatSetting() {
    return {
      mode: FlowStatisticsMode.Users,
      from: this.defaultFrom,
      to: this.defaultTo,
      valueType: FlowStatValueType.Fixed,
      user: {
        login: null,
        id: null
      }
    }
  }

  getStatisticsBySelectedMode() {
    if (this.statSetting.mode === FlowStatisticsMode.User) {
      this.getFlowUserStatistics()
    } else {
      this.getConnectionsStatistics()
    }
  }

  getFlowUserStatistics() {
    this.reqFlowUserStatistics(this.statSetting.user.id!)
      .then(res => {
        this.userConnectionsStatistics = res
        const startNodeRecord = this.findStartNodeStatRecord(this.userConnectionsStatistics)

        for (const connection of this.editorView.connections.values()) {
          const inputKey = +connection.connection.input.key
          const outputKey = +connection.connection.output.key

          const record = this.userConnectionsStatistics.find(r => r.output === outputKey && r.input === inputKey)

          const settingsData: UserConnectionStatistic = {
            criteria: FlowStatisticsMode.User,
            raw: record,
            value: this.calculateConnectionValue(startNodeRecord?.times?.length ?? 0, record?.times?.length ?? 0),
            inputKey,
            outputKey
          }

          this.updateConnectionStatisticsData(connection, settingsData)
        }
      })
  }

  getConnectionsStatistics() {
    this.reqConnectionsStatistics()
      .then(data => {
        this.connectionsStatistics = data

        const startNodeRecord = this.findStartNodeStatRecord(this.connectionsStatistics)

        for (const connection of this.editorView.connections.values()) {
          const inputKey = +connection.connection.input.key
          const outputKey = +connection.connection.output.key

          const record = this.connectionsStatistics.find(r => r.output === outputKey && r.input === inputKey)
          const criteria = this.statSetting.mode as ConnectionStatistic['criteria']

          const settingsData: ConnectionStatistic = {
            criteria,
            value: this.calculateConnectionValue(startNodeRecord?.[criteria] ?? 0, record?.[criteria] ?? 0),
            inputKey,
            outputKey
          }

          this.updateConnectionStatisticsData(connection, settingsData)
        }
      })
  }

  calculateConnectionValue(total:number, value: number) {
    if (this.statSetting.valueType === FlowStatValueType.Fixed) {
      return value
    } else {
      const result = Math.round(value * 100 / total)

      return Number.isNaN(result) ? 0 : result
    }
  }

  findStartNodeStatRecord(records: Array<any>) {
    const startNode = FlowStatistics.StartNode

    return records.find(r => r.output === +(startNode?.mainOutput ?? 0))
  }

  prepareUserStatForFrontend(statistic: UserConnectionStatistic) {
    if (statistic.raw) {
      return Object.entries(groupBy(statistic.raw.times.map(i => i.split(' ')[0])))
        .map(([ date, values ]) => ({
          date,
          user: values.length
        }))
    }

    return []
  }

  async getConnectionStatistics(inputKey: CustomInput['key'], outputKey: CustomOutput['key']) {
    const input = +inputKey
    const output = +outputKey

    return await this.reqConnectionStatistics(input, output)
  }

  resetStatistics() {
    this.connectionsStatistics = null
    this.userConnectionsStatistics = null
  }

  resetState() {
    this.statSetting = this.defaultStatSetting()

    this.resetStatistics()
    this.clearConnectionsStatistics()
  }

  updateConnectionStatisticsData(connection: ConnectionView, statisticsData: ConnectionStatisticsData) {
    Vue.set((connection.connection as CustomConnection).data, 'statistics', statisticsData)

    this.editorView.trigger('updateconnection', {
      connection: connection.connection,
      el: connection.el,
      points: connection.getPoints()
    })
  }

  clearConnectionsStatistics() {
    for (const connection of this.editorView.connections.values()) {
      this.updateConnectionStatisticsData(connection, null)
    }
  }
}
